/**
 * A `serverTimestamp()` write reads back as null locally until the server
 * acknowledges it, so a freshly created document has no sortable time yet.
 *
 * Treat that as "just now" rather than as zero. Sorting it as zero would place
 * a brand-new item at the wrong end of the list and then visibly jump it into
 * place when the server confirms — a new participant flashing at the top of the
 * roster before snapping to the bottom.
 */
export function pendingAsNow(timestampMs: number | null): number {
  return timestampMs ?? Number.MAX_SAFE_INTEGER
}

/**
 * Chronological order for anything Firestore-backed, oldest first.
 *
 * The document id breaks ties deliberately: two documents written in the same
 * millisecond — or two still pending, both reading as MAX_SAFE_INTEGER — would
 * otherwise sort arbitrarily, so two clients could page through the same frames
 * in different orders while believing they were on the same one.
 */
function chronologically<T extends { id: string }>(timeOf: (item: T) => number | null) {
  return (a: T, b: T): number =>
    pendingAsNow(timeOf(a)) - pendingAsNow(timeOf(b)) || a.id.localeCompare(b.id)
}

export const byCreatedAt = chronologically<{ id: string; createdAt: number | null }>(
  (item) => item.createdAt
)

export const byJoinedAt = chronologically<{ id: string; joinedAt: number | null }>(
  (item) => item.joinedAt
)
